"use client";

import { cloudinaryLoader } from "@shared/utils.cloudinary";
import Image from "next/image";
import React from "react";

import * as styles from "./Tile.css";

type TileProps = {
  imagePath?: string;
  altText?: string;
  title?: string;
  link?: string;
  height?: number;
  width?: number;
  onClick?: () => void;
  renderLink?: (props: {
    href: string;
    children: React.ReactNode;
    className?: string;
    "data-title"?: string;
    onClick?: () => void;
  }) => React.ReactNode;
};

/**
 * Tile component for displaying an image with optional title and link.
 *
 * @remarks
 * - Uses Next.js `Image` for optimized image rendering.
 * - Supports custom link rendering via `renderLink` prop.
 * - Falls back to a placeholder if no image is provided.
 *
 * @param imagePath - The path or URL to the image to display.
 * @param altText - Alternative text for the image (for accessibility).
 * @param title - Optional title to display below the image.
 * @param link - Optional URL to wrap the tile with a link.
 * @param height - Height of the image in pixels (default: 96).
 * @param width - Width of the image in pixels (default: 96).
 * @param onClick - Optional click handler for the tile.
 * @param renderLink - Optional function to render a custom link component.
 *
 * @example
 * ```tsx
 * <Tile
 *   imagePath="/images/sample.png"
 *   altText="Sample"
 *   title="Sample Tile"
 *   link="/details"
 *   onClick={() => alert('Tile clicked!')}
 *   renderLink={({ href, children, ...props }) => (
 *     <a href={href} {...props}>{children}</a>
 *   )}
 * />
 * ```
 */
export const Tile: React.FC<TileProps> = ({
  imagePath,
  altText,
  title,
  link,
  height = 96,
  width = 96,
  onClick,
  renderLink
}) => {
  const content = (
    <>
      <div>
        {imagePath ? (
          <div
            className={styles.imageContainer}
            // Have to use inline styles here to ensure the image is sized correctly
            // when the image is not square, as the CSS styles are applied after the image is loaded
            // and the image will not be sized correctly if it is not square.
            style={{
              width: `${width}px`,
              height: `${height}px`,
              margin: "auto"
            }}
          >
            <Image
              className={styles.image}
              data-testid="next-image"
              src={imagePath}
              alt={altText ?? ""}
              unoptimized={process.env.STORYBOOK === "true"}
              quality={85}
              fill
              priority
              loader={cloudinaryLoader}
            />
          </div>
        ) : (
          <div
            className={styles.imageContainer}
            style={{ width: `${width}px`, height: `${height}px` }}
          />
        )}
      </div>

      {title !== undefined && (
        <div className={title ? styles.text : styles.missingText}>{title}</div>
      )}
    </>
  );

  if (link && renderLink) {
    return (
      <div className={styles.tileContainer}>
        {renderLink({
          href: link,
          className: styles.linkStyle,
          "data-title": title,
          onClick,
          children: content
        })}
      </div>
    );
  }

  return (
    <div className={styles.tileContainer} onClick={onClick}>
      {content}
    </div>
  );
};
